import axios, { InternalAxiosRequestConfig } from 'axios'
import qs from 'qs'
import { Store } from '@reduxjs/toolkit'
import { QueryClient } from '@tanstack/react-query'
import { AppDispatch, RootState } from '@/store/store'
import { refreshToken } from '@/features/auth/api/refreshToken'
import { getConfigOptions } from '@/features/auth/api/getConfig'
import { authAction } from '@/store/actions/authAction'

let store: Store
let queryClient: QueryClient

export const injectStore = (_store: Store) => {
  store = _store
}

export const injectQueryClient = (_queryClient: QueryClient) => {
  queryClient = _queryClient
}

export const authClient = axios.create({
  baseURL: import.meta.env.VITE_AUTH_AUTHORITY,
  withCredentials: true,
})

export const authAdminClient = axios.create({
  baseURL: import.meta.env.VITE_AUTH_API_URL,
  paramsSerializer: (params) => {
    return qs.stringify(params, { arrayFormat: 'repeat' })
  },
})

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  paramsSerializer: (params) => {
    return qs.stringify(params, { arrayFormat: 'repeat' })
  },
})

const tokenInjectInterceptor = async (config: InternalAxiosRequestConfig) => {
  const dispatch = store.dispatch as AppDispatch
  const state = store.getState() as RootState
  if (!state.auth.accessToken) {
    return config
  }

  const isAccessTokenExpired = new Date(state.auth.accessTokenExpiration * 1000) < new Date()
  const isRefreshTokenExpired = new Date(state.auth.refreshTokenExpiration * 1000) < new Date()
  if (state.auth.accessToken && !isAccessTokenExpired) {
    config.headers['Authorization'] = `Bearer ${state.auth.accessToken}`
  } else if (state.auth.refreshToken && !isRefreshTokenExpired) {
    try {
      const oidcConfig = await queryClient.ensureQueryData(getConfigOptions())
      const res = await refreshToken({ refreshToken: state.auth.refreshToken, config: oidcConfig })
      await dispatch(authAction({ authResponse: res, queryClient })).unwrap()
      config.headers['Authorization'] = `Bearer ${res.access_token}`
    } catch {
      window.location.reload()
    }
  } else {
    window.location.reload()
  }

  return config
}
apiClient.interceptors.request.use(tokenInjectInterceptor)
authAdminClient.interceptors.request.use(tokenInjectInterceptor)

apiClient.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    if (error.response?.status === 401) {
      window.location.reload()
    }
    return Promise.reject(error)
  },
)

export default apiClient
